/**
 * Helper utilities for enabling New Relic in local development environment.
 * Engineers can opt-in to send telemetry from localhost to the dev New Relic app.
 */

const LOCAL_STORAGE_KEY = 'enableNewRelicForLocal';

/**
 * Check if the engineer has opted in to enable New Relic for local development.
 * This checks for:
 * 1. URL parameter: ?enableNewRelic=true
 * 2. localStorage: enableNewRelicForLocal=true
 */
export const shouldEnableNewRelicForLocal = (): boolean => {
    // Check URL parameter first (takes precedence)
    const urlParams = new URLSearchParams(window.location.search);
    const urlParam = urlParams.get('enableNewRelic');
    if (urlParam === 'true') {
        // Persist to localStorage for future sessions
        localStorage.setItem(LOCAL_STORAGE_KEY, 'true');
        return true;
    }
    if (urlParam === 'false') {
        // Disable and clear localStorage
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        return false;
    }

    // Check localStorage
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    return stored === 'true';
};

/**
 * Log the New Relic local development status to console.
 * @param enabled - Whether New Relic is enabled
 * @param appName - Optional New Relic app name
 */
export const logNewRelicLocalStatus = (enabled: boolean, appName?: string): void => {
    if (enabled) {
        // eslint-disable-next-line no-console
        console.log(`%c✓ New Relic enabled for local development`, 'color: #00ff00; font-weight: bold;');
        if (appName) {
            // eslint-disable-next-line no-console
            console.log(`%cApp: ${appName}`, 'color: #00ff00;');
        }
        // eslint-disable-next-line no-console
        console.log(
            '%cTo disable: Add ?enableNewRelic=false to URL or run localStorage.removeItem("enableNewRelicForLocal")',
            'color: #888;'
        );
    } else {
        // eslint-disable-next-line no-console
        console.log(`%c✗ New Relic disabled for local development`, 'color: #ff9800; font-weight: bold;');
        // eslint-disable-next-line no-console
        console.log(
            '%cTo enable: Add ?enableNewRelic=true to URL or run localStorage.setItem("enableNewRelicForLocal", "true")',
            'color: #888;'
        );
    }
};

/**
 * Helper to enable New Relic for local development.
 * Can be called from browser console: window.enableNewRelicForLocal()
 */
export const enableNewRelicForLocal = (): void => {
    localStorage.setItem(LOCAL_STORAGE_KEY, 'true');
    // eslint-disable-next-line no-console
    console.log('New Relic enabled for local development. Reload the page to activate.');
};

/**
 * Helper to disable New Relic for local development.
 * Can be called from browser console: window.disableNewRelicForLocal()
 */
export const disableNewRelicForLocal = (): void => {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    // eslint-disable-next-line no-console
    console.log('New Relic disabled for local development. Reload the page to deactivate.');
};

/**
 * Generate a new session ID and store it in sessionStorage.
 * New Relic will pick up the new session ID on next page load.
 * Can be called from browser console: window.generateNewSessionId()
 *
 * @returns The new session ID
 */
export const generateNewSessionId = (): string => {
    const newSessionId = 'session_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

    // Set in sessionStorage (where the app actually reads from)
    sessionStorage.setItem('dr-app-session-id', newSessionId);

    // Also set in localStorage for consistency
    localStorage.setItem('sessionId', newSessionId);

    // eslint-disable-next-line no-console
    console.log(`%c✓ New session ID generated: ${newSessionId}`, 'color: #00ff00; font-weight: bold;');
    // eslint-disable-next-line no-console
    console.log('%cReload the page for New Relic to pick up the new session ID', 'color: #888;');

    return newSessionId;
};

/**
 * Add helper functions to window object for easy access in browser console.
 */
export const enableLocalDevHelpers = (): void => {
    (window as any).enableNewRelicForLocal = enableNewRelicForLocal;
    (window as any).disableNewRelicForLocal = disableNewRelicForLocal;
    (window as any).generateNewSessionId = generateNewSessionId;
};
